import env from '@/config/env'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'
import {
  type ConnectedAccount,
  type WorkspaceChatDataParts,
  type WorkspaceChatMediaItem,
  type WorkspaceChatNewMessage,
} from '@backend/routes/stream/workspace-chat'
import { type WorkspaceMedia, markAiEdited } from '@backend/lib/db/typeRoots/workspaceMedia'
import {
  type PendingGeneration,
  pendingGenerationStore,
  usePendingGenerationForWorkspace,
} from '@/stores/pendingGenerationStore'
import { trpc, getApiHeaders } from '@/config/trpc'
import { uploadToS3 } from '@/lib/utils'
import { renderCanvasOffscreen } from '@/lib/renderCanvasOffscreen'
import { loadImageDimensionsFromUrl } from '@/lib/editor'
import { presetAspectRatioForSize, toPresetAspectRatio } from '@backend/lib/util/aspectRatio'
import { type LayoutNode } from '@backend/lib/db/typeRoots/layoutNode'
import { Textarea } from '@/components/ui/shadcn/textarea'
import { RunButton } from '@/components/ui/buttons/run-button'
import { type WorkspaceWithChatHistories } from '@/pages/workspace-page/workspace-page'
import { WorkspaceChatInitial } from './workspace-chat-initial'
import { WorkspaceChatMessageItem } from './workspace-chat-message-item'
import { Button } from '@/components/ui/shadcn/button'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import {
  CalendarDays,
  FileText,
  Github,
  Image as ImageIcon,
  Loader2,
  Link2,
  Palette,
  Paperclip,
  X,
} from 'lucide-react'
import { type CaptionRating } from '@backend/lib/ai/tools/captionRating'
import { type WorkspaceCaptions } from '@backend/lib/db/schema/workspace'
import { UpgradeModal } from '@/components/upgrade-modal'
import { MagicIcon } from '@/components/ui/icons/icons-new'
import { PlumeField, PlumeAura } from '@/components/ui/PlumeField'
import { pushEnergy, burstEnergy } from '@/lib/energyBus'

interface Props {
  workspaceId: number
  moodboardId: number | null
  captions: WorkspaceCaptions
  media: WorkspaceMedia[]
  youtubeTitle?: string
  selectedCaptionPlatform: PlatformType
  connectedAccounts: ConnectedAccount[]
  chatHistory?: WorkspaceWithChatHistories['chatHistory']
  onCaptionChange: (value: string, platform: PlatformType) => void
  onTitleChange?: (value: string, platform: PlatformType) => void
  /**
   * True while the agent is actively writing captions into the editor (from
   * the first postPlan/caption chunk until the post is applied or the stream
   * ends). The page uses it to keep workspace refetches from clobbering the
   * streamed state.
   */
  onAgentEditingChange: (active: boolean) => void
  /** Live caption text streamed by the agent's generatePost tool — UI state only, no save. */
  onCaptionStream: (platform: PlatformType, text: string, youtubeTitle?: string) => void
  /** The generation plan settled (platforms + title) — captions stream next. */
  onPostPlan: (plan: { title: string; platforms: PlatformType[] }) => void
  /** Final generated post — apply to workspace state and persist. */
  onPostGenerated: (
    payload: WorkspaceChatDataParts['postGenerated'],
    userMedia: WorkspaceMedia[],
  ) => Promise<void> | void
  /** Publish the current workspace post immediately (agent's publishPost tool). */
  onPublishPost: (input: {
    platform: PlatformType
    accountId: string
  }) => Promise<{ success: true; link?: string } | { success: false; error: string }>
  onRatingChange: (rating: CaptionRating) => void
  onRatingLoadingChange: (isLoading: boolean) => void
  onRatingAnalyzeRef?: React.MutableRefObject<(() => void) | null>
  onDesignGenerating: (id: string, prompt: string) => void
  onDesignGenerated: (
    id: string,
    result: { slides: { nodes: unknown[]; aspectRatio: string }[] },
  ) => void
  onDesignGenerationFailed: (id: string) => void
  /** Remove an in-progress placeholder without a success/failure toast (design edits). */
  onDesignEditingDone: (id: string) => void
  /** Replace the media item at `index` in place and persist (design edits + undo/redo). */
  onReplaceMedia: (index: number, media: WorkspaceMedia) => Promise<void> | void
  /** Append a new media item and persist (editImage without a target). */
  onAppendMedia: (media: WorkspaceMedia) => Promise<void> | void
  onSchedulePost: (input: {
    platform: PlatformType
    accountId: string
    scheduledAt: string
  }) => Promise<
    | { success: true; scheduledPostId: number; platform: string; scheduledAt: string }
    | { success: false; error: string }
  >
  isRatingLoading?: boolean
}

// NOTE: set true for mockup data if you need work on styling
const USE_HARDCODED_MESSAGES = false

const HARDCODED_MESSAGES: WorkspaceChatNewMessage[] = [
  {
    id: '1',
    role: 'user',
    parts: [{ type: 'text', text: 'Can you make this caption more engaging for LinkedIn?' }],
  },
  {
    id: '2',
    role: 'assistant',
    parts: [
      {
        type: 'text',
        text: "I'll help you create a more engaging LinkedIn caption. Let me analyze your current caption and suggest improvements.",
      },
      {
        type: 'tool-updateLinkedInCaption',
        toolCallId: 'tool-call-1',
        state: 'result',
        input: {
          caption:
            "🚀 Exciting news! Just launched our latest product and I couldn't be more thrilled to share it with you all.\n\nAfter months of hard work and dedication, we've created something truly special that I believe will make a real difference.\n\nWhat do you think? Drop a comment below! 👇",
        },
      },
    ],
  },
  {
    id: '3',
    role: 'user',
    parts: [{ type: 'text', text: 'Can you make it shorter and more professional?' }],
  },
  {
    id: '4',
    role: 'assistant',
    parts: [
      {
        type: 'text',
        text: "Absolutely! Here's a more concise, professional version:",
      },
      {
        type: 'tool-updateLinkedInCaption',
        toolCallId: 'tool-call-2',
        state: 'result',
        input: {
          caption:
            "Thrilled to announce the launch of our latest product. This represents months of development and we're excited to see how it will benefit our community.\n\nWhat are your thoughts?",
        },
      },
    ],
  },
  {
    id: '5',
    role: 'user',
    parts: [{ type: 'text', text: 'Perfect! Thanks' }],
  },
  {
    id: '6',
    role: 'assistant',
    parts: [
      {
        type: 'text',
        text: "You're welcome! Feel free to ask if you need any other adjustments to your caption.",
      },
    ],
  },
] as WorkspaceChatNewMessage[]

export function WorkspaceChat({
  workspaceId,
  moodboardId,
  captions,
  media,
  youtubeTitle,
  selectedCaptionPlatform,
  connectedAccounts,
  chatHistory,
  onCaptionChange,
  onTitleChange,
  onAgentEditingChange,
  onCaptionStream,
  onPostPlan,
  onPostGenerated,
  onPublishPost,
  onRatingChange,
  onRatingLoadingChange,
  onRatingAnalyzeRef,
  onDesignGenerating,
  onDesignGenerated,
  onDesignGenerationFailed,
  onDesignEditingDone,
  onReplaceMedia,
  onAppendMedia,
  onSchedulePost,
  isRatingLoading = false,
}: Props) {
  // refs
  const lastSavedMessagesRef = useRef<WorkspaceChatNewMessage[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const isSendingRef = useRef(false)
  const autoAnalysisInProgressRef = useRef(false)

  const [prompt, setPrompt] = useState('')
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeLimitType, setUpgradeLimitType] = useState('')

  // Mirror chatStatus into a ref so the chatHistory sync effect can read the
  // latest value without depending on it. Including chatStatus in that effect's
  // deps would cause it to re-run on every streaming → ready transition and
  // clobber the freshly-streamed messages with stale chatHistory.
  const chatStatusRef = useRef<string>('ready')
  // Mirror of the live messages for the same reason: the sync effect must
  // detect when local messages are AHEAD of the persisted history.
  const chatMessagesRef = useRef<WorkspaceChatNewMessage[]>([])

  // Latest captions / platform via refs: tool-call handlers run from stream
  // callbacks whose closures can be stale, and the generatePost flow updates
  // captions mid-stream (right before the agent calls generateDesign).
  const captionsRef = useRef<WorkspaceCaptions>(captions)
  captionsRef.current = captions
  const selectedPlatformRef = useRef<PlatformType>(selectedCaptionPlatform)
  selectedPlatformRef.current = selectedCaptionPlatform
  const youtubeTitleRef = useRef<string | undefined>(youtubeTitle)
  youtubeTitleRef.current = youtubeTitle

  // Undo/redo state for updateCaptions tool calls, keyed by toolCallId. The
  // before/after values live in a ref (session-only — after a reload old cards
  // lose their toggle); the version map is state so the cards re-render.
  const captionEditsRef = useRef<
    Record<
      string,
      {
        previous: { captions: Partial<Record<PlatformType, string>>; youtubeTitle?: string }
        next: { captions: Partial<Record<PlatformType, string>>; youtubeTitle?: string }
        showing: 'new' | 'old'
      }
    >
  >({})
  const [captionEditVersions, setCaptionEditVersions] = useState<Record<string, 'new' | 'old'>>({})
  // Confirm/Cancel answers per askConfirmation toolCallId (drives button state).
  const [confirmationAnswers, setConfirmationAnswers] = useState<
    Record<string, 'confirmed' | 'declined'>
  >({})

  // Latest media via ref for tool-call handlers (same staleness rationale as
  // captionsRef above).
  const mediaRef = useRef<WorkspaceMedia[]>(media)
  mediaRef.current = media

  // One-time reference images attached in the composer. Session-scoped: they
  // stay available (and listed in the agent's context) until removed. Not
  // workspace media — they only feed image generation as references.
  type ChatAttachment = {
    id: string
    name: string
    url: string
    key: string
    status: 'uploading' | 'ready'
  }
  const [attachments, setAttachments] = useState<ChatAttachment[]>([])
  const attachmentsRef = useRef<ChatAttachment[]>(attachments)
  attachmentsRef.current = attachments
  const attachmentInputRef = useRef<HTMLInputElement>(null)

  const readyAttachments = () => attachmentsRef.current.filter((a) => a.status === 'ready')

  // Context view: indexes are positions in the ready list — the same mapping
  // the editImage handler uses to resolve attachmentIndexes. Stable callback
  // (reads only refs) so memoized senders can depend on it.
  const attachmentsContext = useCallback(
    () =>
      attachmentsRef.current
        .filter((a) => a.status === 'ready')
        .map((a, index) => ({ index, name: a.name, url: a.url })),
    [],
  )

  // Undo/redo state for editDesign tool calls, keyed by toolCallId (same
  // pattern as caption edits: values in a ref, versions in state).
  const designEditsRef = useRef<
    Record<
      string,
      { index: number; previous: WorkspaceMedia; next: WorkspaceMedia; showing: 'new' | 'old' }
    >
  >({})
  const [designEditVersions, setDesignEditVersions] = useState<Record<string, 'new' | 'old'>>({})
  // Last editImage tool call per media index — lets a revise ("actually...")
  // start from the pre-edit image instead of stacking on the bad result.
  const lastImageEditByIndexRef = useRef<Record<number, string>>({})

  // The photo node of a canvas: largest image node, preferring role 'image'
  // (autodesign's photo slot). -1 when the canvas has no image nodes.
  const findPhotoNodeIndex = (nodes: LayoutNode[]): number => {
    const pickLargest = (indexes: number[]) =>
      indexes.reduce(
        (best, i) =>
          best === -1 ||
          nodes[i]!.width * nodes[i]!.height > nodes[best]!.width * nodes[best]!.height
            ? i
            : best,
        -1,
      )
    const imageNodeIndexes = nodes
      .map((n, i) => (n.data.type === 'image' ? i : -1))
      .filter((i) => i !== -1)
    const photoNodeIndexes = imageNodeIndexes.filter((i) => nodes[i]!.metadata?.role === 'image')
    return pickLargest(photoNodeIndexes.length > 0 ? photoNodeIndexes : imageNodeIndexes)
  }

  // The image URL a media item can contribute (transplants, design features):
  // image items directly; canvases via their photo node. The UI wraps
  // generated images into single-image canvases, so canvases often ARE images.
  const mediaImageSourceUrl = (item: WorkspaceMedia | undefined): string | undefined => {
    if (!item) return undefined
    if (item.type === 'image') return item.file.url
    if (item.type === 'canvas') {
      const photoIndex = findPhotoNodeIndex(item.nodes)
      const photoNode = photoIndex !== -1 ? item.nodes[photoIndex] : undefined
      if (photoNode?.data.type === 'image') return photoNode.data.source.url
    }
    return undefined
  }

  // Compact media view sent with every message: metadata for the system-prompt
  // block, urls for the server-side viewMedia tool.
  const mediaContextRef = useRef<WorkspaceChatMediaItem[]>([])
  mediaContextRef.current = media.map((m, index) => ({
    index,
    type: m.type,
    url: m.file?.url,
    ...(m.type === 'canvas'
      ? {
          aspectRatio: m.aspectRatio,
          origin: m.origin,
          // A canvas holding nothing but image node(s) reads as a plain image
          // to the user — the agent must treat it as one.
          imageOnly: m.nodes.length > 0 && m.nodes.every((n) => n.data.type === 'image'),
        }
      : {}),
  }))

  // Pre-edit captions snapshotted at the FIRST streamed chunk per platform.
  // By the time onToolCall fires, the live input-streaming has already written
  // the new text into the editor, so capturing "previous" there would capture
  // the new text and make undo a no-op.
  const streamingEditOriginalsRef = useRef<
    Record<string, { captions: Partial<Record<PlatformType, string>>; youtubeTitle?: string }>
  >({})

  // Home-page hand-off: the pending generation request for this workspace.
  const pendingGeneration = usePendingGenerationForWorkspace(workspaceId)
  // Captured at send time so the postGenerated handler still has the uploaded
  // file metadata after the store entry is marked consumed.
  const pendingRequestRef = useRef<PendingGeneration | null>(null)
  // Explicit aspect-ratio choice from the home page overrides every design
  // the agent generates in this workspace session ('auto' keeps tool input).
  const designAspectRatioOverrideRef = useRef<PendingGeneration['designAspectRatio']>('auto')

  const { data: moodboard } = trpc.moodboard.getById.useQuery(
    { id: moodboardId! },
    { enabled: !!moodboardId },
  )

  const utils = trpc.useUtils()

  const { mutateAsync: createWorkspaceMessage } = trpc.workspaceMessages.create.useMutation()
  const { mutateAsync: clearWorkspaceMessages, isPending: isClearingChat } =
    trpc.workspaceMessages.clear.useMutation()
  const { mutateAsync: generateCanvas } = trpc.socialMediaPost.generateCanvas.useMutation()
  const { mutateAsync: editCanvas } = trpc.socialMediaPost.editCanvas.useMutation()
  const { mutateAsync: generateImage } = trpc.socialMediaPost.generateImage.useMutation()
  const { mutateAsync: updateImage } = trpc.socialMediaPost.updateImage.useMutation()
  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()
  const { mutateAsync: copyAsset } = trpc.asset.copyAsset.useMutation()
  const { mutateAsync: deleteAsset } = trpc.asset.delete.useMutation()

  const handleAttachmentSelect = async (files: FileList) => {
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue
      const id = `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      setAttachments((prev) => [
        ...prev,
        { id, name: file.name, url: '', key: '', status: 'uploading' },
      ])
      try {
        const { key, uploadUrl, assetUrl } = await getUploadUrl({
          type: 'workspace',
          workspaceId,
        })
        await uploadToS3(uploadUrl, file)
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, url: assetUrl, key, status: 'ready' } : a)),
        )
      } catch (error) {
        console.error('Failed to upload attachment:', error)
        setAttachments((prev) => prev.filter((a) => a.id !== id))
        setError(`Failed to upload ${file.name}. Please try again.`)
      }
    }
  }

  const removeAttachment = (id: string) => {
    const attachment = attachmentsRef.current.find((a) => a.id === id)
    setAttachments((prev) => prev.filter((a) => a.id !== id))
    if (attachment?.key) {
      deleteAsset({ keys: [attachment.key] }).catch((error) => {
        console.error('Failed to delete attachment asset:', error)
      })
    }
  }

  // Raw base64, no data: prefix — both editCanvas and autodesign's
  // referenceImage expect it stripped (passing a full data URL corrupts the
  // image bytes server-side and the provider rejects it).
  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    const response = await fetch(url)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]!)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  // Full data URL (media type preserved) — the generateImage/updateImage
  // routes parse data URLs and need the media type for reference images.
  const fetchImageAsDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  // Resolve editImage attachmentIndexes (positions in the ready list — same
  // mapping as attachmentsContext) into data URLs for the image routes.
  const buildAttachmentReferences = async (attachmentIndexes?: number[]) => {
    const ready = readyAttachments()
    return Promise.all(
      (attachmentIndexes ?? []).map(async (attachmentIndex) => {
        const attachment = ready[attachmentIndex]
        if (!attachment) {
          throw new Error(`No chat attachment at index ${attachmentIndex}`)
        }
        return fetchImageAsDataUrl(attachment.url)
      }),
    )
  }

  // Flip an updateCaptions edit between its new and previous version. One
  // toggle swaps every platform included in that tool call together, so the
  // user compares coherent before/after states.
  const handleToggleCaptionEdit = (toolCallId: string) => {
    const edit = captionEditsRef.current[toolCallId]
    if (!edit) return
    const showing = edit.showing === 'new' ? 'old' : 'new'
    edit.showing = showing
    const values = showing === 'new' ? edit.next : edit.previous
    for (const [platform, text] of Object.entries(values.captions) as [PlatformType, string][]) {
      onCaptionChange(text, platform)
    }
    if (values.youtubeTitle !== undefined) {
      onTitleChange?.(values.youtubeTitle, 'youtube')
    }
    setCaptionEditVersions((prev) => ({ ...prev, [toolCallId]: showing }))
  }

  // Flip an editDesign result between the new and previous design in place.
  const handleToggleDesignEdit = (toolCallId: string) => {
    const edit = designEditsRef.current[toolCallId]
    if (!edit) return
    const showing = edit.showing === 'new' ? 'old' : 'new'
    edit.showing = showing
    void onReplaceMedia(edit.index, showing === 'new' ? edit.next : edit.previous)
    setDesignEditVersions((prev) => ({ ...prev, [toolCallId]: showing }))
  }

  // Best caption to design against: the selected platform's if present, else
  // the first populated one (mirrors the old home-page priority order).
  const pickDesignCaption = () => {
    const c = captionsRef.current
    return (
      c[selectedPlatformRef.current] ||
      c.linkedin ||
      c.instagram ||
      c.tiktok ||
      c.youtube ||
      c.facebook ||
      ''
    )
  }

  // Final payload of the agent's generatePost tool: hand the result (plus any
  // media the user attached on the home page) to the workspace for persistence,
  // then release generation-only context assets (docs/PDFs) and the store slot.
  const handlePostGenerated = async (payload: WorkspaceChatDataParts['postGenerated']) => {
    const request = pendingRequestRef.current
    pendingRequestRef.current = null
    const files = request?.files ?? []
    const isMediaFile = (type: string) => type.startsWith('image/') || type.startsWith('video/')

    const userMedia: WorkspaceMedia[] = files
      .filter((f) => isMediaFile(f.type))
      .map((f) => ({
        type: f.type.startsWith('image/') ? 'image' : 'video',
        file: {
          source: 'remote',
          url: f.url,
          key: f.key,
          file: { name: f.name, type: f.type, size: f.size },
        },
      }))

    try {
      await onPostGenerated(payload, userMedia)
    } catch (error) {
      console.error('Failed to apply generated post:', error)
      setError('Your post was generated but could not be saved. Please try again.')
    } finally {
      onAgentEditingChange(false)
    }

    const docKeys = files.filter((f) => !isMediaFile(f.type)).map((f) => f.key)
    if (docKeys.length > 0) {
      deleteAsset({ keys: docKeys }).catch((error) => {
        console.error('Failed to clean up generation context assets:', error)
      })
    }

    pendingGenerationStore.getState().clearPending(workspaceId)
    utils.usageLimit.get.invalidate()
  }

  const {
    messages: chatMessages,
    status: chatStatus,
    sendMessage,
    addToolOutput,
    setMessages,
    stop,
    error: chatError,
  } = useChat<WorkspaceChatNewMessage>({
    transport: new DefaultChatTransport({
      api: `${env.VITE_BACKEND_URL}/stream/workspace-chat`,
      headers: getApiHeaders,
    }),
    onError: (error) => {
      console.error('Chat error:', error)
      onAgentEditingChange(false)
      try {
        const parsed = JSON.parse(error.message)
        if (parsed.error === 'USAGE_LIMIT_EXCEEDED') {
          setUpgradeLimitType(parsed.limitType || 'chat')
          setShowUpgradeModal(true)
          isSendingRef.current = false
          return
        }
      } catch {
        // Not JSON, fall through
      }
      if (error.message?.includes('Usage limit exceeded')) {
        setUpgradeLimitType('chat')
        setShowUpgradeModal(true)
      } else {
        setError(
          error.message || 'An error occurred while generating a response. Please try again.',
        )
      }
      isSendingRef.current = false
    },
    onFinish: () => {
      isSendingRef.current = false
      setError(null)
      // Safety net: never leave the editor gated against hydration after the
      // stream ends (postGenerated normally clears it, but an aborted or
      // failed generation wouldn't).
      onAgentEditingChange(false)
      // Strip the base64 images out of viewMedia outputs once the turn is
      // over: the model has already SEEN them (via toModelOutput), but left in
      // the message they'd be re-sent as huge JSON text on every following
      // request and persisted into chat history.
      setMessages((prev) =>
        prev.map((message) => {
          if (message.role !== 'assistant') return message
          let changed = false
          const parts = message.parts.map((part) => {
            if (
              part.type === 'tool-viewMedia' &&
              part.state === 'output-available' &&
              part.output?.images?.some((img) => img.data !== '')
            ) {
              changed = true
              return {
                ...part,
                output: {
                  images: part.output.images.map((img) => ({ ...img, data: '' })),
                  failures: part.output.failures,
                },
              }
            }
            return part
          })
          return changed ? { ...message, parts } : message
        }),
      )
      // If rating wasn't updated, reset loading state
      if (autoAnalysisInProgressRef.current) {
        onRatingLoadingChange(false)
        autoAnalysisInProgressRef.current = false
      }
    },
    onData: (dataPart) => {
      // Transient data parts from the agent's generatePost tool — they drive
      // the "agent writes into your post" editor experience.
      if (dataPart.type === 'data-captionStream') {
        onAgentEditingChange(true)
        const { platform, text, youtubeTitle: streamedTitle } = dataPart.data
        onCaptionStream(platform, text, streamedTitle)
      } else if (dataPart.type === 'data-postPlan') {
        onAgentEditingChange(true)
        onPostPlan(dataPart.data)
      } else if (dataPart.type === 'data-postGenerated') {
        void handlePostGenerated(dataPart.data)
      }
    },
    onToolCall: async ({ toolCall }) => {
      if (toolCall.dynamic) {
        return
      }

      try {
        if (toolCall.toolName === 'askConfirmation') {
          // Acknowledge immediately — the real answer arrives as the user's
          // next message via the Confirm/Cancel buttons on the chat card.
          addToolOutput({
            tool: 'askConfirmation',
            toolCallId: toolCall.toolCallId,
            output: { acknowledged: true },
          })
        } else if (toolCall.toolName === 'updateCaptions') {
          const input = toolCall.input
          const previous: (typeof captionEditsRef.current)[string]['previous'] = { captions: {} }
          const next: (typeof captionEditsRef.current)[string]['next'] = { captions: {} }

          // Prefer the pre-stream snapshots: the live input-streaming has
          // already replaced the editor text by the time this runs.
          const originals = streamingEditOriginalsRef.current[toolCall.toolCallId]
          delete streamingEditOriginalsRef.current[toolCall.toolCallId]

          for (const p of [
            'instagram',
            'linkedin',
            'tiktok',
            'youtube',
            'facebook',
            'meta-ads',
          ] as const) {
            const value = input[p]
            if (typeof value !== 'string') continue
            previous.captions[p] = originals?.captions[p] ?? captionsRef.current[p] ?? ''
            next.captions[p] = value
            onCaptionChange(value, p)
          }
          if (typeof input.youtubeTitle === 'string') {
            previous.youtubeTitle = originals?.youtubeTitle ?? youtubeTitleRef.current ?? ''
            next.youtubeTitle = input.youtubeTitle
            onTitleChange?.(input.youtubeTitle, 'youtube')
          }

          captionEditsRef.current[toolCall.toolCallId] = { previous, next, showing: 'new' }
          setCaptionEditVersions((prev) => ({ ...prev, [toolCall.toolCallId]: 'new' }))
          onAgentEditingChange(false)

          addToolOutput({
            tool: 'updateCaptions',
            toolCallId: toolCall.toolCallId,
            output: { success: true },
          })
        } else if (toolCall.toolName === 'captionRating') {
          onRatingChange(toolCall.input)
          onRatingLoadingChange(false)
          autoAnalysisInProgressRef.current = false

          addToolOutput({
            tool: 'captionRating',
            toolCallId: toolCall.toolCallId,
            output: { success: true },
          })
        } else if (toolCall.toolName === 'generateDesign') {
          const generationId = `design-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

          onDesignGenerating(generationId, toolCall.input.prompt || 'Designing graphic...')

          const aspectRatioOverride = designAspectRatioOverrideRef.current

          try {
            // Images the design must feature: workspace image items and/or
            // chat attachments, resolved to URLs for autodesign's
            // existingImages reuse path.
            const ready = readyAttachments()
            const existingImages = [
              ...(toolCall.input.includeMediaIndexes ?? []).flatMap((mediaIndex) => {
                const url = mediaImageSourceUrl(mediaRef.current[mediaIndex])
                return url ? [url] : []
              }),
              ...(toolCall.input.includeAttachmentIndexes ?? []).flatMap((attachmentIndex) => {
                const attachment = ready[attachmentIndex]
                return attachment ? [attachment.url] : []
              }),
            ]

            const result = await generateCanvas({
              caption: pickDesignCaption(),
              prompt: toolCall.input.prompt,
              format: toolCall.input.format,
              count: toolCall.input.count,
              existingImages: existingImages.length > 0 ? existingImages : undefined,
              aspectRatio:
                aspectRatioOverride !== 'auto' ? aspectRatioOverride : toolCall.input.aspectRatio,
              workspaceId,
              moodboardId: moodboardId ?? undefined,
            })

            utils.usageLimit.get.invalidate()

            if (!result.ok) {
              onDesignGenerationFailed(generationId)
              setUpgradeLimitType(result.limitType)
              setShowUpgradeModal(true)
              addToolOutput({
                tool: 'generateDesign',
                toolCallId: toolCall.toolCallId,
                output: { success: false, error: 'Usage limit exceeded' },
              })
              return
            }

            // Nodes that reused a provided image keep it, but via an asset
            // copy so deleting the source media later can't dangle the node.
            // (Whether the image is featured at all is the layout model's
            // design decision — it now SEES the provided images.)
            if (existingImages.length > 0) {
              try {
                for (const slide of result.slides) {
                  for (const node of slide.nodes as LayoutNode[]) {
                    if (
                      node.data.type === 'image' &&
                      existingImages.includes(node.data.source.url)
                    ) {
                      const copied = await copyAsset({
                        sourceUrl: node.data.source.url,
                        destination: { type: 'workspace', workspaceId },
                      })
                      node.data.source = { type: 'url', url: copied.url }
                    }
                  }
                }
              } catch (copyError) {
                console.warn('Failed to copy reused image asset:', copyError)
              }
            }

            onDesignGenerated(generationId, { slides: result.slides })

            addToolOutput({
              tool: 'generateDesign',
              toolCallId: toolCall.toolCallId,
              output: { success: true },
            })
          } catch (designError) {
            onDesignGenerationFailed(generationId)
            const errorMessage =
              designError instanceof Error ? designError.message : String(designError)
            addToolOutput({
              tool: 'generateDesign',
              toolCallId: toolCall.toolCallId,
              output: { success: false, error: errorMessage },
            })
          }
        } else if (toolCall.toolName === 'editDesign') {
          const { index, instruction, mode } = toolCall.input
          const item = mediaRef.current[index]

          if (!item || item.type === 'video' || item.type === 'document') {
            addToolOutput({
              tool: 'editDesign',
              toolCallId: toolCall.toolCallId,
              output: {
                success: false,
                error: item
                  ? `Media [${index}] is a ${item.type} and cannot be design-edited`
                  : `No media at index ${index}`,
              },
            })
            return
          }

          const generationId = `edit-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
          onDesignGenerating(generationId, 'Editing design...')

          try {
            const renderedBase64 = await fetchImageAsBase64(item.file.url)

            let newNodes: LayoutNode[] | null = null
            let newAspectRatio: string =
              item.type === 'canvas' ? toPresetAspectRatio(item.aspectRatio) : '1:1'
            let usedMode: 'edited' | 'redesigned' = 'redesigned'

            // Tier 1 — surgical node edit. Only canvas media have nodes; a
            // needsRedesign escape falls through to regeneration below.
            if (item.type === 'canvas' && mode === 'targeted' && item.nodes.length > 0) {
              const result = await editCanvas({
                workspaceId,
                instruction,
                nodes: item.nodes,
                aspectRatio: toPresetAspectRatio(item.aspectRatio),
                renderedImage: renderedBase64,
              })
              if (result.status === 'edited') {
                newNodes = result.nodes
                usedMode = 'edited'
              }
            }

            // Tier 2 — reference regeneration (restyle mode, plain images, or
            // targeted requests the planner escalated).
            if (!newNodes) {
              const existingImages =
                item.type === 'canvas'
                  ? item.nodes
                      .filter(
                        (n) =>
                          n.data.type === 'image' &&
                          n.data.source.type === 'url' &&
                          n.metadata?.role === 'image',
                      )
                      .map((n) => (n.data as { source: { url: string } }).source.url)
                  : [item.file.url]

              if (item.type === 'image') {
                try {
                  const dims = await loadImageDimensionsFromUrl(item.file.url)
                  newAspectRatio = presetAspectRatioForSize(dims.width, dims.height)
                } catch {
                  // keep 1:1
                }
              }

              const result = await generateCanvas({
                caption: pickDesignCaption() || instruction,
                prompt: instruction,
                referenceImage: renderedBase64,
                referenceFidelity: 90,
                existingImages: existingImages.length > 0 ? existingImages : undefined,
                aspectRatio: toPresetAspectRatio(newAspectRatio),
                workspaceId,
                moodboardId: moodboardId ?? undefined,
              })
              utils.usageLimit.get.invalidate()

              if (!result.ok) {
                onDesignEditingDone(generationId)
                setUpgradeLimitType(result.limitType)
                setShowUpgradeModal(true)
                addToolOutput({
                  tool: 'editDesign',
                  toolCallId: toolCall.toolCallId,
                  output: { success: false, error: 'Usage limit exceeded' },
                })
                return
              }
              newNodes = result.nodes as LayoutNode[]
              newAspectRatio = result.aspectRatio
            }

            const file = await renderCanvasOffscreen({
              nodes: newNodes,
              aspectRatio: newAspectRatio,
              aiProvenance: markAiEdited(item),
            })
            const { key, uploadUrl, assetUrl } = await getUploadUrl({
              type: 'workspace',
              workspaceId,
            })
            await uploadToS3(uploadUrl, file)

            const newMedia: WorkspaceMedia = {
              type: 'canvas',
              nodes: newNodes,
              aspectRatio: newAspectRatio,
              file: {
                source: 'remote',
                url: assetUrl,
                key,
                file: { name: file.name, type: file.type, size: file.size },
              },
              ...markAiEdited(item),
            }

            designEditsRef.current[toolCall.toolCallId] = {
              index,
              previous: item,
              next: newMedia,
              showing: 'new',
            }
            setDesignEditVersions((prev) => ({ ...prev, [toolCall.toolCallId]: 'new' }))

            await onReplaceMedia(index, newMedia)
            onDesignEditingDone(generationId)

            addToolOutput({
              tool: 'editDesign',
              toolCallId: toolCall.toolCallId,
              output: { success: true, mode: usedMode },
            })
          } catch (editError) {
            onDesignEditingDone(generationId)
            const errorMessage = editError instanceof Error ? editError.message : String(editError)
            addToolOutput({
              tool: 'editDesign',
              toolCallId: toolCall.toolCallId,
              output: { success: false, error: errorMessage },
            })
          }
        } else if (toolCall.toolName === 'editImage') {
          const {
            index,
            prompt: imagePrompt,
            editCurrent,
            revise,
            fromMediaIndex,
            attachmentIndexes,
          } = toolCall.input

          // No index → create a brand-new standalone image media item.
          if (index === undefined) {
            const generationId = `image-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
            onDesignGenerating(generationId, 'Generating image...')
            try {
              const referenceImages = await buildAttachmentReferences(attachmentIndexes)
              const result = await generateImage({
                model: referenceImages.length > 0 ? 'gemini' : 'flux',
                prompt: imagePrompt,
                caption: pickDesignCaption() || imagePrompt,
                referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
                workspaceId,
              })
              utils.usageLimit.get.invalidate()

              if (!result.ok) {
                onDesignEditingDone(generationId)
                setUpgradeLimitType(result.limitType)
                setShowUpgradeModal(true)
                addToolOutput({
                  tool: 'editImage',
                  toolCallId: toolCall.toolCallId,
                  output: { success: false, error: 'Usage limit exceeded' },
                })
                return
              }

              await onAppendMedia({ type: 'image', file: result.file })
              onDesignEditingDone(generationId)
              addToolOutput({
                tool: 'editImage',
                toolCallId: toolCall.toolCallId,
                output: {
                  success: true,
                  created: true,
                  imagePrompt: result.enhancedPrompt ?? imagePrompt,
                },
              })
            } catch (imageError) {
              onDesignEditingDone(generationId)
              addToolOutput({
                tool: 'editImage',
                toolCallId: toolCall.toolCallId,
                output: {
                  success: false,
                  error: imageError instanceof Error ? imageError.message : String(imageError),
                },
              })
            }
            return
          }

          const item = mediaRef.current[index]

          // revise → re-edit from the base the LAST edit started from, so
          // corrections don't stack on a bad result. Resolved from the
          // persisted chat history (each editImage output records its
          // baseImageUrl) — in-memory refs don't survive reloads/HMR.
          let reviseBaseUrl: string | undefined
          if (revise) {
            outer: for (let mi = chatMessagesRef.current.length - 1; mi >= 0; mi--) {
              const message = chatMessagesRef.current[mi]!
              if (message.role !== 'assistant') continue
              for (let pi = message.parts.length - 1; pi >= 0; pi--) {
                const part = message.parts[pi]
                if (
                  part?.type === 'tool-editImage' &&
                  part.state === 'output-available' &&
                  part.output?.success === true &&
                  part.input?.index === index &&
                  part.output.baseImageUrl
                ) {
                  reviseBaseUrl = part.output.baseImageUrl
                  break outer
                }
              }
            }
          }

          // For the undo toggle: when the pre-edit media object is still known
          // (same session), a revise's undo compares against it instead of the
          // rejected edit.
          const lastEditId = lastImageEditByIndexRef.current[index]
          const lastEdit = lastEditId ? designEditsRef.current[lastEditId] : undefined
          const undoBase =
            revise && lastEdit && lastEdit.index === index ? lastEdit.previous : undefined

          // For canvas targets: the photo node to swap.
          const targetNodeIndex = item?.type === 'canvas' ? findPhotoNodeIndex(item.nodes) : -1

          // Transplant source (fromMediaIndex): any media that can contribute
          // an image — a plain image item, an image-only canvas (the UI wraps
          // generated images this way), or a design (its photo is extracted).
          const transplantSourceUrl =
            fromMediaIndex !== undefined
              ? mediaImageSourceUrl(mediaRef.current[fromMediaIndex])
              : undefined

          const validationError = !item
            ? `No media at index ${index}`
            : item.type !== 'image' && item.type !== 'canvas'
              ? `Media [${index}] is a ${item.type} and has no editable image`
              : item.type === 'canvas' && targetNodeIndex === -1
                ? `Design [${index}] has no photo element — use editDesign or generateDesign instead`
                : fromMediaIndex !== undefined && item.type !== 'canvas'
                  ? 'fromMediaIndex requires the target to be a canvas design'
                  : fromMediaIndex !== undefined && !transplantSourceUrl
                    ? `Media [${fromMediaIndex}] has no image that could be transplanted`
                    : null
          if (validationError || !item || (item.type !== 'image' && item.type !== 'canvas')) {
            addToolOutput({
              tool: 'editImage',
              toolCallId: toolCall.toolCallId,
              output: { success: false, error: validationError ?? 'Invalid editImage target' },
            })
            return
          }

          const generationId = `image-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
          onDesignGenerating(generationId, 'Working on the image...')

          try {
            let newImageUrl: string
            let newImageFile: Extract<WorkspaceMedia, { type: 'image' }>['file'] | null = null
            let usedImagePrompt: string | undefined
            let usedBaseImageUrl: string | undefined

            if (transplantSourceUrl) {
              // Direct transplant — no generation, exact pixels. The asset is
              // COPIED so deleting the source media later can't leave the
              // design's node pointing at a deleted S3 object.
              const copied = await copyAsset({
                sourceUrl: transplantSourceUrl,
                destination: { type: 'workspace', workspaceId },
              })
              newImageUrl = copied.url
            } else {
              const referenceImages = await buildAttachmentReferences(attachmentIndexes)

              const sourceUrl =
                reviseBaseUrl ??
                (item.type === 'image'
                  ? item.file.url
                  : (item.nodes[targetNodeIndex]!.data as { source: { url: string } }).source.url)

              // Preserve the source's aspect ratio — without this every edit
              // snaps back to the 1:1 default.
              let aspect: string | undefined
              try {
                const dims = await loadImageDimensionsFromUrl(sourceUrl)
                if (dims.width > 0 && dims.height > 0) {
                  aspect = `${dims.width}:${dims.height}`
                }
              } catch {
                // keep provider default
              }

              const result = editCurrent
                ? await updateImage({
                    model: 'gemini',
                    prompt: imagePrompt,
                    image: await fetchImageAsDataUrl(sourceUrl),
                    referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
                    ...(aspect ? { fluxOptions: { aspect } } : {}),
                    workspaceId,
                  })
                : await generateImage({
                    // Gemini handles multi-reference composition best; Flux is
                    // the default for pure prompt-to-image (mirrors MCP).
                    model: referenceImages.length > 0 ? 'gemini' : 'flux',
                    prompt: imagePrompt,
                    caption: pickDesignCaption() || imagePrompt,
                    referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
                    ...(aspect ? { fluxOptions: { aspect } } : {}),
                    workspaceId,
                  })
              utils.usageLimit.get.invalidate()

              if (!result.ok) {
                onDesignEditingDone(generationId)
                setUpgradeLimitType(result.limitType)
                setShowUpgradeModal(true)
                addToolOutput({
                  tool: 'editImage',
                  toolCallId: toolCall.toolCallId,
                  output: { success: false, error: 'Usage limit exceeded' },
                })
                return
              }
              newImageUrl = result.url
              newImageFile = result.file
              usedImagePrompt = result.enhancedPrompt ?? imagePrompt
              // Record the base this edit started from so a later revise can
              // return to it (persisted with the message, unlike refs).
              if (editCurrent) {
                usedBaseImageUrl = sourceUrl
              }
            }

            let newMedia: WorkspaceMedia
            if (item.type === 'image') {
              // Carries provenance forward. This used to write no `origin` and no
              // `hasSyntheticEdits` at all, so an AI-edited image looked like a
              // plain upload: no IPTC marker, no disclosure default, and the
              // settings dialog reporting "no AI detected" on AI-edited media.
              newMedia = {
                type: 'image',
                file: newImageFile!,
                ...markAiEdited(item),
              }
            } else {
              const newNodes = structuredClone(item.nodes)
              const targetNode = newNodes[targetNodeIndex]!
              if (targetNode.data.type === 'image') {
                targetNode.data.source = { type: 'url', url: newImageUrl }
              }
              const file = await renderCanvasOffscreen({
                nodes: newNodes,
                aspectRatio: item.aspectRatio,
                aiProvenance: markAiEdited(item),
              })
              const { key, uploadUrl, assetUrl } = await getUploadUrl({
                type: 'workspace',
                workspaceId,
              })
              await uploadToS3(uploadUrl, file)
              newMedia = {
                type: 'canvas',
                nodes: newNodes,
                aspectRatio: item.aspectRatio,
                file: {
                  source: 'remote',
                  url: assetUrl,
                  key,
                  file: { name: file.name, type: file.type, size: file.size },
                },
                ...markAiEdited(item),
              }
            }

            designEditsRef.current[toolCall.toolCallId] = {
              index,
              // For a revise, undo compares against the pre-edit original
              // (when the session still knows it) — comparing with the
              // rejected edit would be useless.
              previous: undoBase ?? item,
              next: newMedia,
              showing: 'new',
            }
            setDesignEditVersions((prev) => ({ ...prev, [toolCall.toolCallId]: 'new' }))
            lastImageEditByIndexRef.current[index] = toolCall.toolCallId

            await onReplaceMedia(index, newMedia)
            onDesignEditingDone(generationId)

            addToolOutput({
              tool: 'editImage',
              toolCallId: toolCall.toolCallId,
              output: {
                success: true,
                ...(usedImagePrompt ? { imagePrompt: usedImagePrompt } : {}),
                ...(usedBaseImageUrl ? { baseImageUrl: usedBaseImageUrl } : {}),
              },
            })
          } catch (imageError) {
            onDesignEditingDone(generationId)
            const errorMessage =
              imageError instanceof Error ? imageError.message : String(imageError)
            addToolOutput({
              tool: 'editImage',
              toolCallId: toolCall.toolCallId,
              output: { success: false, error: errorMessage },
            })
          }
        } else if (toolCall.toolName === 'publishPost') {
          try {
            const result = await onPublishPost({
              platform: toolCall.input.platform,
              accountId: toolCall.input.accountId,
            })
            addToolOutput({
              tool: 'publishPost',
              toolCallId: toolCall.toolCallId,
              output: result,
            })
          } catch (publishError) {
            const errorMessage =
              publishError instanceof Error ? publishError.message : String(publishError)
            addToolOutput({
              tool: 'publishPost',
              toolCallId: toolCall.toolCallId,
              output: { success: false, error: errorMessage },
            })
          }
        } else if (toolCall.toolName === 'schedulePost') {
          try {
            const result = await onSchedulePost({
              platform: toolCall.input.platform,
              accountId: toolCall.input.accountId,
              scheduledAt: toolCall.input.scheduledAt,
            })

            addToolOutput({
              tool: 'schedulePost',
              toolCallId: toolCall.toolCallId,
              output: result,
            })
          } catch (scheduleError) {
            const errorMessage =
              scheduleError instanceof Error ? scheduleError.message : String(scheduleError)
            addToolOutput({
              tool: 'schedulePost',
              toolCallId: toolCall.toolCallId,
              output: { success: false, error: errorMessage },
            })
          }
        }
      } catch (error) {
        console.error('Error handling tool call:', error)
        setError('An error occurred while processing the tool call. Please try again.')
      }
    },
  })

  // Keep the refs in sync so the chatHistory effect below can read the latest
  // values without taking them as deps.
  chatStatusRef.current = chatStatus
  chatMessagesRef.current = chatMessages

  useEffect(() => {
    if (!workspaceId) {
      setMessages([])
      lastSavedMessagesRef.current = []
      setError(null)
      setPrompt('')
      return
    }

    // Don't overwrite in-flight messages during streaming or tool execution
    // (e.g. when React Query refetches on window focus)
    if (chatStatusRef.current === 'streaming' || isSendingRef.current) {
      return
    }

    // A refetch can deliver history that is BEHIND the live conversation
    // (message saves are async; workspace.get invalidations happen mid-turn).
    // Never let stale history clobber messages that only exist locally.
    const historyMessages =
      chatHistory && chatHistory.length > 0 && Array.isArray(chatHistory[0]?.messages)
        ? (chatHistory[0]!.messages as WorkspaceChatNewMessage[])
        : []
    const incomingIds = new Set(historyMessages.map((m) => m.id))
    const localIsAhead = chatMessagesRef.current.some((m) => m.id && !incomingIds.has(m.id))
    if (localIsAhead) {
      return
    }

    if (historyMessages.length === 0) {
      setMessages([])
      lastSavedMessagesRef.current = []
      setError(null)
      return
    }

    setMessages(historyMessages)
    lastSavedMessagesRef.current = historyMessages
    setError(null)
  }, [workspaceId, chatHistory, setMessages])

  useEffect(() => {
    if (chatError) {
      setError(chatError.message || 'An error occurred. Please try again.')
      isSendingRef.current = false
    }
  }, [chatError])

  // Live-stream updateCaptions tool input into the editor while the model is
  // still writing the arguments (input-streaming state). Mirrors the
  // generatePost data-part streaming, but here the caption text IS the tool
  // input, so we read the partial parse straight off the message part.
  useEffect(() => {
    if (chatStatus !== 'streaming') return
    const last = chatMessages[chatMessages.length - 1]
    if (!last || last.role !== 'assistant') return
    for (const part of last.parts) {
      if (part.type !== 'tool-updateCaptions' || part.state !== 'input-streaming') continue
      const input = (part.input ?? {}) as Partial<Record<string, string>>
      onAgentEditingChange(true)
      const originals = (streamingEditOriginalsRef.current[part.toolCallId] ??= { captions: {} })
      for (const p of [
        'instagram',
        'linkedin',
        'tiktok',
        'youtube',
        'facebook',
        'meta-ads',
      ] as const) {
        const value = input[p]
        if (typeof value === 'string' && value !== '') {
          // Snapshot the pre-edit text the first time this platform appears —
          // captionsRef still holds the original at that point.
          if (!(p in originals.captions)) {
            originals.captions[p] = captionsRef.current[p] ?? ''
            if (p === 'youtube') {
              originals.youtubeTitle = youtubeTitleRef.current ?? ''
            }
          }
          onCaptionStream(p, value, p === 'youtube' ? input['youtubeTitle'] : undefined)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages, chatStatus])

  // Home-page hand-off: send the parked generation request as the first agent
  // message once the uploads are done (status 'ready') and — when a moodboard
  // backs this workspace — its brief has loaded, so the agent has the brand
  // voice from message one. Marking the store entry 'sent' makes this one-shot.
  useEffect(() => {
    if (!pendingGeneration || pendingGeneration.status !== 'ready') return
    if (isSendingRef.current || chatStatusRef.current === 'streaming') return
    if (moodboardId && !moodboard) return

    pendingRequestRef.current = pendingGeneration
    designAspectRatioOverrideRef.current = pendingGeneration.designAspectRatio
    pendingGenerationStore.getState().updatePending(workspaceId, { status: 'sent' })

    setError(null)
    isSendingRef.current = true
    burstEnergy()

    const hasUserMedia = pendingGeneration.files.some(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/'),
    )

    sendMessage(
      { text: pendingGeneration.prompt },
      {
        body: {
          context: {
            ...captionsRef.current,
            youtubeTitle,
            moodboardBrief: moodboard?.analysisBrief
              ? JSON.parse(JSON.stringify(moodboard.analysisBrief))
              : null,
            moodboardId,
            media: mediaContextRef.current,
            attachments: attachmentsContext(),
            workspaceId,
            selectedPlatform: selectedPlatformRef.current,
            connectedAccounts,
            generation: {
              topic: pendingGeneration.prompt,
              contextFiles: pendingGeneration.files.map((f) => ({ url: f.url })),
              contextURLs: pendingGeneration.contextURLs,
              style: pendingGeneration.style,
              hasUserMedia,
            },
          },
        },
      },
    ).catch((error) => {
      console.error('Error sending generation request:', error)
      setError(error instanceof Error ? error.message : 'Failed to start post generation.')
      isSendingRef.current = false
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGeneration, moodboard, moodboardId, workspaceId, youtubeTitle, connectedAccounts])

  const triggerRatingAnalysis = useCallback(() => {
    if (
      autoAnalysisInProgressRef.current ||
      chatStatus === 'streaming' ||
      isSendingRef.current ||
      !sendMessage
    ) {
      return
    }

    const currentCaption = captions[selectedCaptionPlatform] ?? ''
    if (!currentCaption.trim()) {
      return
    }

    autoAnalysisInProgressRef.current = true
    onRatingLoadingChange(true)

    sendMessage(
      {
        text: `Please analyse and rate the current ${selectedCaptionPlatform} caption.`,
      },
      {
        body: {
          context: {
            ...captions,
            youtubeTitle,
            moodboardBrief: moodboard?.analysisBrief
              ? JSON.parse(JSON.stringify(moodboard.analysisBrief))
              : null,
            moodboardId,
            media: mediaContextRef.current,
            attachments: attachmentsContext(),
            workspaceId,
            selectedPlatform: selectedCaptionPlatform,
            connectedAccounts,
          },
        },
      },
    )
  }, [
    captions,
    youtubeTitle,
    selectedCaptionPlatform,
    connectedAccounts,
    chatStatus,
    sendMessage,
    moodboard,
    moodboardId,
    workspaceId,
    attachmentsContext,
    onRatingLoadingChange,
  ])

  // Expose the function via ref
  useEffect(() => {
    if (onRatingAnalyzeRef) {
      onRatingAnalyzeRef.current = triggerRatingAnalysis
    }
  }, [triggerRatingAnalysis, onRatingAnalyzeRef])

  useEffect(() => {
    if (chatMessages.length === 0) {
      return
    }

    const lastMessage = chatMessages[chatMessages.length - 1]
    if (!lastMessage || !lastMessage.id) return

    const alreadySaved = lastSavedMessagesRef.current.find((m) => m.id === lastMessage.id)
    if (alreadySaved) return

    if (lastMessage.role === 'user') {
      saveMessage(lastMessage)
        .catch((error) => {
          console.error('Failed to save user message:', error)
        })
        .then(() => {
          if (lastMessage.id) {
            lastSavedMessagesRef.current.push(lastMessage)
          }
        })
      return
    }

    if (lastMessage.role === 'assistant' && chatStatus !== 'streaming') {
      saveMessage(lastMessage)
        .catch((error) => {
          console.error('Failed to save assistant message:', error)
        })
        .then(() => {
          if (lastMessage.id) {
            lastSavedMessagesRef.current.push(lastMessage)
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatStatus, chatMessages])

  const baseMessages = USE_HARDCODED_MESSAGES ? HARDCODED_MESSAGES : chatMessages
  const hasMessages = baseMessages.length > 0

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [baseMessages, chatStatus, isRatingLoading])

  const saveMessage = async (message: WorkspaceChatNewMessage) => {
    if (!message || !message.id) return

    try {
      const cleanMessage = JSON.parse(JSON.stringify(message))

      await createWorkspaceMessage({
        workspaceId: workspaceId,
        message: cleanMessage,
      })
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('circular')) {
        console.error('Failed to save message: Circular reference detected', {
          messageId: message.id,
          role: message.role,
        })
        setError(
          'Failed to save message due to data serialization error. Your message was sent but not saved.',
        )
      } else {
        console.error('Failed to save message:', error)
      }
    }
  }

  const handleClearChat = async () => {
    await clearWorkspaceMessages({ workspaceId })
    setMessages([])
    lastSavedMessagesRef.current = []
    utils.workspace.get.invalidate({ id: workspaceId })
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && chatStatus === 'streaming') {
        console.log('Tab hidden while streaming')
      } else if (!document.hidden && chatStatus === 'streaming') {
        console.log('Tab visible while streaming')
      }
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (chatStatus === 'streaming') {
        e.preventDefault()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (chatStatus === 'streaming') {
        try {
          stop()
        } catch (error) {
          console.error('Error stopping chat on unmount:', error)
        }
      }
    }
  }, [chatStatus, stop])

  const handleSendMessage = async (messageText?: string) => {
    if (typeof messageText === 'object' && messageText !== null) {
      console.warn('handleSendMessage received an event object instead of a string')
      return
    }

    const textToSend = messageText || prompt.trim()
    if (!textToSend) return

    if (isSendingRef.current || chatStatus === 'streaming') {
      return
    }

    if (!messageText) {
      setPrompt('')
    }

    setError(null)
    isSendingRef.current = true
    // The plume field surges the moment a message goes out — the agent should
    // feel like it activates instantly, even while the model is still thinking.
    burstEnergy()

    try {
      const cleanBrief = moodboard?.analysisBrief
        ? JSON.parse(JSON.stringify(moodboard.analysisBrief))
        : null

      await sendMessage(
        {
          text: textToSend,
        },
        {
          body: {
            context: {
              ...captions,
              youtubeTitle,
              moodboardBrief: cleanBrief,
              moodboardId,
              media: mediaContextRef.current,
              attachments: attachmentsContext(),
              workspaceId,
              selectedPlatform: selectedCaptionPlatform,
              connectedAccounts,
            },
          },
        },
      )
    } catch (error) {
      console.error('Error sending message:', error)
      setError(error instanceof Error ? error.message : 'Failed to send message. Please try again.')
      isSendingRef.current = false
    }
  }

  const handleApplyCaption = (captionToApply: string, platform: PlatformType, title?: string) => {
    onCaptionChange(captionToApply, platform)
    if (title && onTitleChange) {
      onTitleChange(title, platform)
    }
  }

  // Confirm/Cancel button click on an askConfirmation card: the answer goes
  // back to the agent as a regular user message, so the conversation keeps
  // its natural shape and the agent decides how to proceed.
  const handleConfirmationAnswer = (toolCallId: string, confirmed: boolean) => {
    if (confirmationAnswers[toolCallId]) return
    setConfirmationAnswers((prev) => ({
      ...prev,
      [toolCallId]: confirmed ? 'confirmed' : 'declined',
    }))
    handleSendMessage(confirmed ? 'Yes, go ahead.' : "No, don't do that.")
  }

  const isStreaming = chatStatus === 'streaming'
  const isDisabled = !prompt.trim() || isStreaming || isSendingRef.current

  // Instant feedback between sending a message and the agent's first visible
  // output (the model may spend seconds thinking before any token arrives).
  const lastBaseMessage = baseMessages[baseMessages.length - 1]
  const hasVisibleAssistantReply =
    lastBaseMessage?.role === 'assistant' &&
    lastBaseMessage.parts.some(
      (p) => (p.type === 'text' && p.text !== '') || p.type.startsWith('tool-'),
    )
  const isAwaitingAgent =
    (chatStatus === 'submitted' || chatStatus === 'streaming') && !hasVisibleAssistantReply

  // Active context surfaced to the user for transparency: the moodboard driving generation and
  // the live sources the agent can reach (GitHub connectors + content-source URLs). This mirrors
  // what the backend actually gates on, so what's shown is what the agent can use.
  const moodboardConnectors = (moodboard?.items ?? []).flatMap((i) =>
    i.type === 'connector'
      ? [
          {
            id: i.id,
            provider: i.provider,
            label: i.label || i.target.split('/').pop() || i.target,
          },
        ]
      : [],
  )
  const moodboardContentUrls = (moodboard?.items ?? []).flatMap((i) => {
    if (i.type !== 'url' || i.role !== 'content') return []
    let host = i.url
    try {
      host = new URL(i.url).hostname.replace(/^www\./, '')
    } catch {
      // keep the raw url
    }
    return [{ id: i.id, label: host }]
  })

  return (
    <div className="border-border bg-background relative h-full w-full transform overflow-hidden rounded-lg border transition-transform duration-300 ease-in-out">
      <PlumeField className="bottom-28 left-1/2 h-[26%] w-[min(720px,104%)] -translate-x-1/2" />
      <div className="relative z-10 flex h-full flex-col">
        {/* Chat Messages */}
        <div
          ref={messagesContainerRef}
          className="messages-scroll flex-1 overflow-y-auto px-2 pt-4">
          {error && (
            <div className="mb-4 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
              <div className="font-semibold">Error</div>
              <div>{error}</div>
              <button
                onClick={() => {
                  setError(null)
                }}
                className="mt-2 text-xs underline">
                Dismiss
              </button>
            </div>
          )}
          {hasMessages ? (
            <div className="">
              {baseMessages.map((message) => {
                if (!message || !message.id) {
                  console.warn('Invalid message found:', message)
                  return null
                }
                return (
                  <WorkspaceChatMessageItem
                    key={message.id}
                    message={message}
                    currentCaptions={captions}
                    onApplyCaption={handleApplyCaption}
                    isStreaming={isStreaming}
                    captionEditVersions={captionEditVersions}
                    onToggleCaptionEdit={handleToggleCaptionEdit}
                    designEditVersions={designEditVersions}
                    onToggleDesignEdit={handleToggleDesignEdit}
                    confirmationAnswers={confirmationAnswers}
                    onConfirmationAnswer={handleConfirmationAnswer}
                  />
                )
              })}
              {/* Agent activation indicator: covers the silent gap between
                  sending and the first streamed token */}
              {isAwaitingAgent && (
                <div className="mb-4 flex justify-start">
                  <div className="max-w-[420px] p-2">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <MagicIcon color="var(--primary)" width={14} height={14} />
                        <div className="text-primary animate-pulse text-sm">Thinking...</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Rating Analysis Loading Indicator */}
              {isRatingLoading && (
                <div className="mb-4 flex justify-start">
                  <div className="max-w-[420px] p-2">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="text-primary h-4 w-4 animate-spin" />
                        <div className="text-primary text-sm">Analyzing post rating...</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Empty div at the bottom for auto-scroll */}
              <div ref={messagesEndRef} />
            </div>
          ) : (
            <WorkspaceChatInitial onSendMessage={handleSendMessage} />
          )}
        </div>

        {/* Active-context pills: the moodboard driving generation + the live sources the
            agent can reach (GitHub connectors, content-source URLs). Mirrors the backend's
            moodboard-gated access, so what's shown is what the agent can actually use. */}
        {moodboard && (
          <div className="flex flex-wrap items-center gap-1.5 px-2 pb-1.5">
            <span className="border-border bg-card text-foreground inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm">
              <Palette size={14} className="text-muted-foreground" />
              {moodboard.name}
            </span>
            {moodboardConnectors.map((c) => (
              <span
                key={c.id}
                className="border-border bg-card text-foreground inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm">
                {c.provider === 'github' ? (
                  <Github size={14} className="text-muted-foreground" />
                ) : c.provider === 'google_calendar' ? (
                  <CalendarDays size={14} className="text-muted-foreground" />
                ) : (
                  <FileText size={14} className="text-muted-foreground" />
                )}
                {c.label}
              </span>
            ))}
            {moodboardContentUrls.map((c) => (
              <span
                key={c.id}
                className="border-border bg-card text-foreground inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm">
                <Link2 size={14} className="text-muted-foreground" />
                {c.label}
              </span>
            ))}
          </div>
        )}

        {/* Input Area */}
        <div className="relative m-2">
          <PlumeAura focused={isInputFocused} />
          <div className="border-border bg-card relative flex flex-col gap-2 rounded-lg border">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-2 pt-2">
                {attachments.map((attachment) => (
                  <span
                    key={attachment.id}
                    className="bg-secondary text-secondary-foreground flex items-center gap-1 rounded-full px-2 py-0.5 text-xs">
                    {attachment.status === 'uploading' ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <ImageIcon className="h-3 w-3" />
                    )}
                    <span className="max-w-[120px] truncate">{attachment.name}</span>
                    <button
                      onClick={() => removeAttachment(attachment.id)}
                      className="hover:text-foreground transition-colors">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <Textarea
              value={prompt}
              onChange={(e) => {
                pushEnergy()
                setPrompt(e.target.value)
              }}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  if (!isDisabled) {
                    handleSendMessage()
                  }
                }
              }}
              placeholder="Ask me what I can do..."
              className="min-h-[60px] resize-none border-none bg-transparent text-sm"
            />
            <div className="flex w-full justify-end gap-2 p-2">
              <input
                ref={attachmentInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) void handleAttachmentSelect(e.target.files)
                  e.target.value = ''
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground self-center"
                title="Attach reference images"
                onClick={() => attachmentInputRef.current?.click()}>
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground self-center font-light"
                disabled={isClearingChat}
                onClick={handleClearChat}>
                Clear Conversation
              </Button>

              <RunButton
                onClick={() => handleSendMessage()}
                disabled={
                  USE_HARDCODED_MESSAGES
                    ? !prompt.trim()
                    : !prompt.trim() || isStreaming || isSendingRef.current
                }
              />
            </div>
          </div>
        </div>
      </div>
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType={upgradeLimitType}
        surface="workspace_chat"
      />
    </div>
  )
}
